/**
 * Page-specific help content for the contextual help system.
 * Keyed by route path (matching ROUTE_META in app-shell.tsx).
 */

export interface HelpConcept {
  readonly term: string;
  readonly description: string;
}

export interface HelpPageContent {
  readonly title: string;
  readonly description: string;
  readonly concepts: readonly HelpConcept[];
  readonly steps: readonly string[];
  readonly roles: readonly ("admin" | "technician" | "auditor")[];
  readonly relatedPages?: readonly { label: string; href: string }[];
}

export const HELP_CONTENT: Record<string, HelpPageContent> = {
  "/dashboard": {
    title: "Dashboard",
    description:
      "Your central hub for monitoring the GDAP/WIF environment. Admins see organisation-wide stats, pending actions, and customer activity. Technicians see their own sessions and recent activity.",
    concepts: [
      {
        term: "Attention Required",
        description:
          "Items needing immediate action — pending PIM approval requests and GDAP relationships expiring within 30 days.",
      },
      {
        term: "Recent Activity",
        description:
          "A filtered view of deliberate user actions such as creating GDAP relationships, activating PIM sessions, and managing technicians. Technicians only see their own activity.",
      },
      {
        term: "Customer Activity",
        description:
          "A summary of which customers have the most recent activity, helping spot unusual patterns or high-usage tenants.",
      },
      {
        term: "System Health",
        description:
          "Real-time status of backend services: HaloPSA API, Microsoft Graph, Redis Cache, Azure SQL, and SignalR.",
      },
    ],
    steps: [
      "Review the stat cards at the top for a quick overview of customers, sessions, technicians, and pending reviews.",
      "Check the Attention Required panel for items needing your action.",
      "Review Recent Activity for the latest user actions across the platform.",
      "Monitor System Health to ensure all services are operational.",
    ],
    roles: ["admin", "technician", "auditor"],
    relatedPages: [
      { label: "PIM Approvals", href: "/pim-approvals" },
      { label: "GDAP Relationships", href: "/gdap" },
    ],
  },

  "/customers": {
    title: "Customers",
    description:
      "View and search all managed customer tenants synced from HaloPSA. Each customer has a GDAP configuration defining their access tier, strategy, and session settings.",
    concepts: [
      {
        term: "Service Tier",
        description:
          "The customer's service level (Enterprise, Business, Essential) which determines the maximum role bundles available for GDAP relationships.",
      },
      {
        term: "Access Strategy",
        description:
          "Controls how GDAP relationships are managed — OPEN (auto-provision), RESTRICTED (manual approval), or BLOCKED (no access).",
      },
      {
        term: "Max Access Level",
        description:
          "The highest permission level allowed for this customer: FullAccess, StandardAccess, LimitedAccess, or ReadOnly.",
      },
      {
        term: "HaloPSA Sync",
        description:
          "Customer data is cached from HaloPSA. Use the Sync button to manually refresh the cache if customer details appear outdated.",
      },
    ],
    steps: [
      "Use the search bar to find customers by name.",
      "Filter by Service Tier or Access Strategy using the dropdowns.",
      "Click a customer row to view their full configuration and GDAP status.",
      "Use the Sync HaloPSA button if customer data appears stale.",
    ],
    roles: ["admin", "technician"],
    relatedPages: [
      { label: "GDAP Relationships", href: "/gdap" },
      { label: "Settings", href: "/settings" },
    ],
  },

  "/customers/$": {
    title: "Customer Detail",
    description:
      "View a customer's full GDAP configuration, active relationships, and access Microsoft admin portals for the customer's tenant.",
    concepts: [
      {
        term: "PIM Approval Required",
        description:
          "When enabled, PIM session activations for this customer require approval from a designated approver before access is granted.",
      },
      {
        term: "GDAP Duration Override",
        description:
          "A customer-specific override for the default GDAP relationship duration in days. If set, new relationships use this duration instead of the global default.",
      },
      {
        term: "Admin Portals",
        description:
          "Quick links to Microsoft admin portals (M365, Azure, Exchange, etc.) pre-configured with the customer's tenant ID for direct access.",
      },
    ],
    steps: [
      "Review the customer's configuration settings at the top.",
      "Check active GDAP relationships and their status.",
      "Use admin portal links to access the customer's Microsoft services.",
      "Click 'Create GDAP Relationship' to set up new delegated access.",
      "Click 'Activate PIM Session' to elevate privileges for this customer.",
    ],
    roles: ["admin", "technician"],
    relatedPages: [
      { label: "Customers", href: "/customers" },
      { label: "GDAP Relationships", href: "/gdap" },
    ],
  },

  "/customers/$customerId/activate": {
    title: "Activate PIM Session",
    description:
      "Elevate your privileges for a customer tenant by activating a PIM (Privileged Identity Management) session. Select your activation tier, choose roles, set duration, and optionally link a HaloPSA ticket.",
    concepts: [
      {
        term: "Activation Tier",
        description:
          "The privilege level for this session. Higher tiers grant access to more powerful roles. You can only activate at or below your assigned tier.",
      },
      {
        term: "Role Selection",
        description:
          "Choose which Entra ID directory roles to activate. Available roles are determined by your tier and the customer's max access level.",
      },
      {
        term: "Session Duration",
        description:
          "How long the elevated access lasts. Maximum duration is set by the customer's configuration. Sessions auto-expire when the timer runs out.",
      },
      {
        term: "HaloPSA Ticket",
        description:
          "Optionally link a HaloPSA ticket number. Session lifecycle events (activate, extend, deactivate) will be posted as comments on the ticket.",
      },
      {
        term: "Approval Required",
        description:
          "If the customer or tier requires approval, your activation request will be held pending until an approver grants access.",
      },
    ],
    steps: [
      "Select your activation tier from the dropdown.",
      "Choose the roles you need for this task.",
      "Set the session duration (up to the customer's maximum).",
      "Optionally enter a HaloPSA ticket number for audit linking.",
      "Provide a justification for the activation.",
      "Click Activate. If approval is required, you'll be notified when approved.",
    ],
    roles: ["admin", "technician"],
    relatedPages: [
      { label: "PIM Sessions", href: "/pim-sessions" },
      { label: "PIM Approvals", href: "/pim-approvals" },
    ],
  },

  "/technicians": {
    title: "Technicians",
    description:
      "Manage technician access levels and attributes. Assign tiers, security clearances, and locations that determine which customers and roles each technician can access.",
    concepts: [
      {
        term: "Technician Tier",
        description:
          "Privilege level assigned to a technician (Tier-1 to Tier-4). Higher tiers can activate more powerful roles. Stored as an Entra ID extension attribute.",
      },
      {
        term: "Security Clearance",
        description:
          "Additional access classification (Standard, Elevated, Critical) controlling access to sensitive customers.",
      },
      {
        term: "Location",
        description:
          "Geographic restriction for the technician, used to enforce data sovereignty requirements.",
      },
      {
        term: "Eligibility",
        description:
          "The computed set of customers and roles a technician can access, derived from their tier, clearance, location, and each customer's configuration.",
      },
    ],
    steps: [
      "Search for a technician by name or use the list.",
      "Click Add Technician to register a new team member from Entra ID.",
      "Click a technician to view or edit their attributes.",
      "Update tier, clearance, or location to change access levels.",
      "Use Revoke Access to completely remove a technician's privileges.",
    ],
    roles: ["admin"],
    relatedPages: [
      { label: "Approval Policies", href: "/approval-policies" },
      { label: "Audit Log", href: "/audit-log" },
    ],
  },

  "/pim-sessions": {
    title: "PIM Sessions",
    description:
      "Monitor all PIM sessions across the organisation. Admins see all sessions; technicians see only their own. Track active, expired, and revoked sessions.",
    concepts: [
      {
        term: "Active Session",
        description:
          "A currently elevated privilege session. The technician has the assigned roles active in the customer's tenant until the session expires or is deactivated.",
      },
      {
        term: "Session Expiry",
        description:
          "Sessions automatically expire after their configured duration. A countdown timer shows remaining time. Sessions can also be manually deactivated or extended.",
      },
      {
        term: "Revocation",
        description:
          "An admin can forcibly revoke a technician's active session, immediately removing the elevated privileges. Revocations are audit-logged.",
      },
    ],
    steps: [
      "Filter sessions by status using the tabs (All, Active, Pending, Expired, Revoked).",
      "Click the Eye icon to view session details and active countdown.",
      "Admins can revoke any active session using the revoke button.",
      "Check the audit log for session lifecycle history.",
    ],
    roles: ["admin", "technician"],
    relatedPages: [
      { label: "PIM Approvals", href: "/pim-approvals" },
      { label: "Audit Log", href: "/audit-log" },
    ],
  },

  "/pim-approvals": {
    title: "PIM Approvals",
    description:
      "Review and action pending PIM activation requests. When a technician requests access to a customer that requires approval, the request appears here for designated approvers to approve or deny.",
    concepts: [
      {
        term: "Approval Queue",
        description:
          "Pending activation requests awaiting your decision. Each request shows the technician, customer, requested tier, and justification.",
      },
      {
        term: "Required Approvals",
        description:
          "Some tiers require multiple approvers. The request shows how many approvals are needed and how many have been granted so far.",
      },
      {
        term: "Request Expiry",
        description:
          "Approval requests expire after a configured timeout. Expired requests must be re-submitted by the technician.",
      },
    ],
    steps: [
      "Review each pending request — check the technician, customer, tier, and justification.",
      "Click Approve to grant the activation. The technician's session will start immediately.",
      "Click Deny and provide a reason to reject the request.",
      "Monitor the approval history to track decisions.",
    ],
    roles: ["admin"],
    relatedPages: [
      { label: "Approval Policies", href: "/approval-policies" },
      { label: "PIM Sessions", href: "/pim-sessions" },
    ],
  },

  "/approval-policies": {
    title: "Approval Policies",
    description:
      "Configure per-tier approval requirements for PIM activations. Define whether each tier requires approval, how many approvers are needed, and which Entra group contains the approvers.",
    concepts: [
      {
        term: "Approval Mode",
        description:
          "Controls whether activations at a given tier require approval: None (auto-approve), Single (one approver), or Multi (multiple approvers required).",
      },
      {
        term: "Approver Group",
        description:
          "An Entra ID security group whose members can approve activation requests for the tier.",
      },
      {
        term: "Max Duration",
        description:
          "The maximum session duration allowed for activations at this tier, in hours.",
      },
      {
        term: "Require Justification",
        description:
          "When enabled, technicians must provide a written justification when requesting activation at this tier.",
      },
    ],
    steps: [
      "Review the current approval policy for each tier.",
      "Click Edit to modify a tier's approval requirements.",
      "Set the approval mode (None, Single, Multi).",
      "Select an approver group from Entra ID.",
      "Configure max duration and justification requirements.",
      "Save changes — they take effect immediately for new activation requests.",
    ],
    roles: ["admin"],
    relatedPages: [
      { label: "PIM Approvals", href: "/pim-approvals" },
      { label: "Technicians", href: "/technicians" },
    ],
  },

  "/gdap": {
    title: "GDAP Relationships",
    description:
      "Manage Granular Delegated Admin Privilege (GDAP) relationships with customer tenants. Create new relationships, monitor their status, and reconcile access assignments.",
    concepts: [
      {
        term: "GDAP Relationship",
        description:
          "A formal delegation of admin privileges from a customer tenant to your MSP. Each relationship has a defined set of roles and a fixed duration.",
      },
      {
        term: "Relationship Status",
        description:
          "Lifecycle states: Creating → Approving (customer must approve) → Active → Expired/Terminated. Approval is done via the customer's Microsoft admin portal.",
      },
      {
        term: "Access Assignments",
        description:
          "Security group-to-role mappings within an active GDAP relationship. Technicians gain roles through group membership.",
      },
      {
        term: "Reconciliation",
        description:
          "Compares current GDAP state in Microsoft Graph against expected configuration. Detects drift (missing assignments, stale groups) and can auto-remediate.",
      },
    ],
    steps: [
      "Search for relationships by customer name.",
      "Filter by status (Active, Approving, Expired, etc.).",
      "Click Create Relationship to start the creation wizard.",
      "Click a relationship to view its detail, assignments, and drift status.",
      "Use Reconcile to detect and fix configuration drift.",
    ],
    roles: ["admin"],
    relatedPages: [
      { label: "Customers", href: "/customers" },
      { label: "Audit Log", href: "/audit-log" },
    ],
  },

  "/gdap/$": {
    title: "GDAP Relationship Detail",
    description:
      "View the full details of a GDAP relationship including its access assignments, security groups, and configuration drift status.",
    concepts: [
      {
        term: "Access Assignments",
        description:
          "The security group-to-role mappings for this relationship. Each assignment links an Entra security group to a set of directory roles at a specific access level.",
      },
      {
        term: "Configuration Drift",
        description:
          "Differences between expected and actual state in Microsoft Graph. Drift can occur when assignments are modified outside this platform or when Graph API operations partially fail.",
      },
      {
        term: "Approval URL",
        description:
          "For relationships in 'Approving' status, this is the URL the customer admin must visit in the Microsoft Admin Portal to approve the GDAP relationship.",
      },
    ],
    steps: [
      "Review the relationship status and duration at the top.",
      "Check Access Assignments to see which security groups have which roles.",
      "Review Configuration Drift for any discrepancies.",
      "Copy the Approval URL and send it to the customer if the relationship is pending approval.",
    ],
    roles: ["admin"],
    relatedPages: [
      { label: "GDAP Relationships", href: "/gdap" },
      { label: "Customers", href: "/customers" },
    ],
  },

  "/audit-log": {
    title: "Audit Log",
    description:
      "Search, filter, and export the tamper-evident audit trail. Every significant action in the platform is recorded with actor, target, timestamp, and severity. The log uses hash-chain integrity verification.",
    concepts: [
      {
        term: "Event Types",
        description:
          "Over 50 event types across authentication, customer config, technician management, GDAP, PIM, approvals, notifications, and scheduled tasks.",
      },
      {
        term: "Severity Levels",
        description:
          "INFO (routine operations), WARNING (unusual but non-critical events), CRITICAL (security-relevant events requiring attention).",
      },
      {
        term: "Hash Chain Integrity",
        description:
          "Each audit entry includes a cryptographic hash linked to the previous entry. This chain can be verified to detect any tampering with the audit trail.",
      },
      {
        term: "Retention Policy",
        description:
          "Configurable per-tenant retention period for audit records. Expired records are automatically purged. Default retention varies by severity.",
      },
    ],
    steps: [
      "Use the search bar and filters to find specific events.",
      "Filter by event type, severity, date range, or actor.",
      "Click Export to download the filtered results as CSV or JSON.",
      "Use Verify Integrity to check the hash chain for tampering.",
      "Configure retention policies under the Retention tab.",
    ],
    roles: ["admin", "auditor"],
    relatedPages: [
      { label: "Settings", href: "/settings" },
      { label: "Scheduled Tasks", href: "/scheduled-tasks" },
    ],
  },

  "/scheduled-tasks": {
    title: "Scheduled Tasks",
    description:
      "Monitor and manage background jobs that run on timers. View execution history, enable/disable tasks, and manually trigger runs.",
    concepts: [
      {
        term: "Task Schedule",
        description:
          "Each task runs on a cron schedule (e.g., every 5 minutes, every 15 minutes). The schedule is defined in the backend and cannot be changed from the UI.",
      },
      {
        term: "Execution History",
        description:
          "A log of recent task runs showing start time, duration, status (success/failure), and any error messages.",
      },
      {
        term: "Manual Trigger",
        description:
          "Run a task immediately outside its normal schedule. Useful for testing or when you need results right away (e.g., cache refresh, reconciliation).",
      },
    ],
    steps: [
      "Review the task list and their last run status.",
      "Click a task to view its execution history.",
      "Toggle the switch to enable or disable a task.",
      "Click Run Now to trigger a task immediately.",
      "Check for failed tasks and review error messages.",
    ],
    roles: ["admin"],
    relatedPages: [
      { label: "Audit Log", href: "/audit-log" },
      { label: "Settings", href: "/settings" },
    ],
  },

  "/settings": {
    title: "Settings",
    description:
      "Configure platform-wide settings including GDAP defaults, notification preferences, and HaloPSA integration settings.",
    concepts: [
      {
        term: "GDAP Duration",
        description:
          "The default duration (in days) for new GDAP relationships. Individual customers can override this with their own duration.",
      },
      {
        term: "Notification Settings",
        description:
          "Configure which events trigger notifications and through which channels (email, HaloPSA ticket, in-app).",
      },
      {
        term: "HaloPSA Ticket Type",
        description:
          "The ticket type used when creating HaloPSA tickets for GDAP approvals and PIM activation tracking.",
      },
    ],
    steps: [
      "Review and update the default GDAP relationship duration.",
      "Configure notification preferences for different event types.",
      "Set the HaloPSA ticket type for automated ticket creation.",
      "Save changes — most settings take effect immediately.",
    ],
    roles: ["admin"],
    relatedPages: [
      { label: "Customers", href: "/customers" },
      { label: "Scheduled Tasks", href: "/scheduled-tasks" },
    ],
  },
};
